// Pilotage des abonnés : fiche d'un côté ou d'une personne, à droite du tableau. Les actions réutilisent les cartes
// existantes (crédits, offre, territoires, suspension), déjà réservées au gestionnaire par la base.
import { useQuery } from "@tanstack/react-query";
import {
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  CreditSide,
  ProjectAssignment,
  Sale,
  SubscriptionRequest,
  UserTerritory,
} from "../types";
import type { AccountCurrency } from "./accountCurrency";
import { SideCard } from "./CreditList";
import { CountryOptions, PartnerCard } from "./PartnerList";
import { formatDay } from "./projectUtils";
import type { Selection } from "./SubscribersPage";
import { subscriptionLabel, territoryLabel } from "./territories";
import {
  costInCurrency,
  costPerCredit,
  formatUnitCost,
  formatCost,
  formatUsd,
} from "./subscribers";

type Month = {
  month: string;
  airscale_usd: number;
  serper_usd: number;
  contacts_ai_usd: number;
  assistant_usd: number;
  base_usd: number;
  cost_usd: number;
  credits_spent: number;
  emails: number;
  mobiles: number;
  teams: number;
};
const COST_LINES = [
  "airscale",
  "serper",
  "contacts_ai",
  "assistant",
  "base",
] as const;

type LedgerLine = {
  created_at: string;
  delta: number;
  reason: string;
  note: string | null;
  person: string;
  author: string | null;
};
type PersonRow = {
  sales_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  disabled: boolean;
  credits_spent: number;
  cost_usd: number;
};
type SidePayload = {
  side: CreditSide & {
    currency: AccountCurrency | null;
    created_at: string | null;
    next_topup: string | null;
  };
  org: { id: number; name: string } | null;
  world: boolean;
  territories: UserTerritory[];
  // Prix compté par territoire, comme les crédits : monde seul, sinon zones et pays.
  offer: {
    plan: "world" | "territories";
    zones: number;
    countries: number;
    commitment: string | null;
    price_eur: number | null;
    price_usd: number | null;
  } | null;
  requests: (Pick<
    SubscriptionRequest,
    "plan" | "commitment" | "country" | "zone"
  > & {
    id: number;
    sales_id: number;
    created_at: string;
  })[];
  months: Month[];
  ledger: LedgerLine[];
  usage: {
    logins: number;
    contact_requests: number;
    contact_requests_found: number;
    questions: number;
    watches: number;
    open_deals: number;
  };
  people: PersonRow[];
};
type PersonPayload = {
  person: PersonRow & {
    side: number;
    partner: boolean;
    administrator: boolean;
    org_admin: boolean;
    partner_offer: string | null;
    invited_at: string | null;
    accepted_at: string | null;
    last_activity: string | null;
    locale: string | null;
    currency: string | null;
    logins: number;
  };
  months: Month[];
  ledger: LedgerLine[];
  requests: {
    created_at: string;
    project_id: string;
    project: string | null;
    status: "pending" | "done" | "error";
    found: number | null;
    credits: number;
  }[];
  questions: {
    created_at: string;
    question: string;
    cost_usd: number | null;
    rating: number | null;
  }[];
};

const personName = (person: {
  first_name: string | null;
  last_name: string | null;
  email: string;
}) =>
  [person.first_name, person.last_name].filter(Boolean).join(" ") ||
  person.email;

/** Fiche à droite du tableau : un côté ou une personne, selon la sélection. */
export const SubscriberPanel = ({
  selected,
  month,
  currency,
  rate,
  sideNames,
  onSelect,
  onClose,
  onChange,
}: {
  selected: Selection;
  month: string;
  currency: AccountCurrency;
  rate: number | null;
  sideNames: Record<number, string>;
  onSelect: (selection: Selection) => void;
  onClose: () => void;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  if (!selected) return null;
  return (
    <aside className="sticky top-4 max-h-[calc(100vh-6rem)] w-[28rem] shrink-0 overflow-y-auto border-l pl-4">
      {/* Liste des pays proposés à la saisie d'un territoire (champ de la carte du partenaire). */}
      <CountryOptions />
      <div className="mb-2 flex justify-end">
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X className="size-4" aria-hidden />
          {translate("resources.projects.subscribers.panel.close")}
        </Button>
      </div>
      {selected.kind === "side" ? (
        <SidePanel
          key={`side-${selected.id}`}
          id={selected.id}
          month={month}
          currency={currency}
          rate={rate}
          onSelect={onSelect}
          onChange={onChange}
        />
      ) : (
        <PersonPanel
          key={`person-${selected.id}`}
          id={selected.id}
          month={month}
          currency={currency}
          rate={rate}
          sideNames={sideNames}
          onSelect={onSelect}
          onChange={onChange}
        />
      )}
    </aside>
  );
};

const SidePanel = ({
  id,
  month,
  currency,
  rate,
  onSelect,
  onChange,
}: {
  id: number;
  month: string;
  currency: AccountCurrency;
  rate: number | null;
  onSelect: (selection: Selection) => void;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { data, error, isPending, refetch } = useQuery({
    queryKey: ["manager_side", id, month],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("manager_side", {
        side: id,
        month,
      });
      if (error) throw error;
      return data as SidePayload;
    },
    retry: false,
  });
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`resources.projects.subscribers.panel.${key}`, args);
  if (error) return <p className="text-sm text-destructive">{error.message}</p>;
  if (isPending || !data) return null;
  const refresh = () => {
    refetch();
    onChange();
  };
  const name =
    data.side.name ?? translate("resources.projects.subscribers.client_team");
  const price = data.offer
    ? currency === "USD"
      ? data.offer.price_usd
      : data.offer.price_eur
    : null;
  const current = data.months[data.months.length - 1];
  const perCredit = current
    ? costPerCredit(current.cost_usd, current.credits_spent)
    : null;
  const usage = data.usage;

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div>
        <h2 className="text-lg font-semibold">{name}</h2>
        {data.offer && (
          <p className="text-sm">
            {data.offer.plan === "world"
              ? translate("resources.projects.pricing.plans.world.name")
              : [
                  data.offer.countries > 0 &&
                    t("offer_countries", { smart_count: data.offer.countries }),
                  data.offer.zones > 0 &&
                    t("offer_zones", { smart_count: data.offer.zones }),
                ]
                  .filter(Boolean)
                  .join(" + ")}
            {data.offer.commitment &&
              ` · ${translate(`resources.projects.pricing.${data.offer.commitment}`).toLocaleLowerCase(locale)}`}
          </p>
        )}
        {price != null ? (
          <p className="text-sm">
            {t("offer_price", {
              amount: new Intl.NumberFormat(locale, {
                style: "currency",
                currency,
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0,
              }).format(price),
            })}{" "}
            <span className="text-muted-foreground text-xs">
              ({t("offer_price_help")})
            </span>
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">{t("no_offer")}</p>
        )}
        {data.side.created_at && (
          <p className="text-muted-foreground text-xs">
            {t("created_at", { date: formatDay(data.side.created_at, locale) })}
          </p>
        )}
        <p className="text-muted-foreground text-xs">
          {data.side.next_topup
            ? t("next_topup", { date: formatDay(data.side.next_topup, locale) })
            : t("no_topup")}
        </p>
        {(data.world || data.territories.length > 0) && (
          <div className="mt-2 flex flex-wrap items-center gap-1">
            <span className="text-muted-foreground text-xs">
              {t("territories")}
            </span>
            {data.world && <Badge>{t("world")}</Badge>}
            {data.territories.map((territory, index) => (
              <Badge key={index} variant="outline">
                {territoryLabel(territory, locale, translate)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <SideCard side={data.side} name={name} canGrant onGranted={refresh} />

      <MonthsBlock
        months={data.months}
        currency={currency}
        rate={rate}
        perCredit={perCredit}
      />

      <div className="flex flex-col gap-1 text-sm">
        <h3 className="font-semibold">{t("usage")}</h3>
        <span>
          {t("contact_requests", {
            count: usage.contact_requests,
            found: usage.contact_requests_found,
          })}
        </span>
        <span>{t("questions", { count: usage.questions })}</span>
        <span>{t("watches", { count: usage.watches })}</span>
        <span>{t("open_deals", { count: usage.open_deals })}</span>
        <span title={t("logins_help")}>
          {t("logins", { count: usage.logins })}
        </span>
      </div>

      <Ledger lines={data.ledger} showPerson />

      {data.requests.length > 0 && (
        <div className="flex flex-col gap-1 text-sm">
          <h3 className="font-semibold">{t("pending_requests")}</h3>
          {data.requests.map((request) => {
            const who = data.people.find(
              (p) => p.sales_id === request.sales_id,
            );
            return (
              <button
                key={request.id}
                type="button"
                className="text-left underline"
                onClick={() =>
                  onSelect({ kind: "person", id: request.sales_id })
                }
              >
                {t("pending_request", {
                  label: subscriptionLabel(
                    request as SubscriptionRequest,
                    locale,
                    translate,
                  ),
                  date: formatDay(request.created_at, locale),
                })}
                {who && ` · ${personName(who)}`}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">{t("people")}</h3>
        {data.people.map((person) => (
          <button
            key={person.sales_id}
            type="button"
            className="hover:bg-muted/50 flex justify-between gap-2 rounded px-1 py-1 text-left text-sm"
            onClick={() => onSelect({ kind: "person", id: person.sales_id })}
          >
            <span className="min-w-0 truncate">
              {personName(person)}
              {person.disabled && (
                <span className="text-muted-foreground ml-1 text-xs">
                  ({t("suspended")})
                </span>
              )}
            </span>
            <span className="text-muted-foreground shrink-0 text-xs">
              {person.credits_spent} ·{" "}
              <span title={formatUsd(person.cost_usd, locale)}>
                {formatCost(person.cost_usd, currency, rate, locale)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const PersonPanel = ({
  id,
  month,
  currency,
  rate,
  sideNames,
  onSelect,
  onChange,
}: {
  id: number;
  month: string;
  currency: AccountCurrency;
  rate: number | null;
  sideNames: Record<number, string>;
  onSelect: (selection: Selection) => void;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const [resending, setResending] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, error, isPending, refetch } = useQuery({
    queryKey: ["manager_person", id, month],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("manager_person", {
        sale: id,
        month,
      });
      if (error) throw error;
      return data as PersonPayload;
    },
    retry: false,
  });
  // Mêmes lectures que l'écran des partenaires, limitées à la personne.
  const {
    data: sale,
    error: saleError,
    refetch: refetchSale,
  } = useGetOne<Sale & { partner: boolean }>("sales", { id });
  const page = { page: 1, perPage: 100 };
  const {
    data: territories,
    error: territoriesError,
    refetch: refetchTerritories,
  } = useGetList<UserTerritory>("user_territories", {
    filter: { sales_id: id },
    pagination: page,
    sort: { field: "country", order: "ASC" },
  });
  const { data: assignments, error: assignmentsError } =
    useGetList<ProjectAssignment>("project_assignments", {
      filter: { sales_id: id },
      pagination: page,
      sort: { field: "assigned_at", order: "DESC" },
    });
  const {
    data: requests,
    error: requestsError,
    refetch: refetchRequests,
  } = useGetList<SubscriptionRequest>("subscription_requests", {
    filter: { sales_id: id, status: "pending" },
    pagination: page,
    sort: { field: "created_at", order: "ASC" },
  });
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`resources.projects.subscribers.panel.${key}`, args);
  const loadError =
    error ?? saleError ?? territoriesError ?? assignmentsError ?? requestsError;
  if (loadError)
    return (
      <p className="text-sm text-destructive">
        {loadError instanceof Error ? loadError.message : String(loadError)}
      </p>
    );
  if (isPending || !data) return null;
  const person = data.person;
  const refresh = () => {
    refetch();
    refetchSale();
    refetchTerritories();
    refetchRequests();
    onChange();
  };
  const current = data.months[data.months.length - 1];
  const perCredit = current
    ? costPerCredit(current.cost_usd, current.credits_spent)
    : null;
  const role = person.partner
    ? person.org_admin
      ? "role_org_admin"
      : "role_partner"
    : person.administrator
      ? "role_administrator"
      : "role_member";

  // Même lien que « mot de passe oublié » : la personne choisit son mot de passe depuis l'e-mail.
  const resend = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().auth.resetPasswordForEmail(
      person.email,
      { redirectTo: `${window.location.origin}/set-password` },
    );
    setBusy(false);
    setResending(false);
    if (error) notify(error.message, { type: "error" });
    else
      notify("resources.projects.subscribers.panel.resend_done", {
        type: "info",
      });
  };

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div>
        <h2 className="text-lg font-semibold">{personName(person)}</h2>
        <p className="text-muted-foreground text-sm">{person.email}</p>
        {sideNames[person.side] && (
          <p className="text-sm">
            {t("side_of")}
            <button
              type="button"
              className="underline"
              onClick={() => onSelect({ kind: "side", id: person.side })}
            >
              {sideNames[person.side]}
            </button>
          </p>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          <Badge variant="outline">{t(role)}</Badge>
          {person.disabled && <Badge variant="outline">{t("suspended")}</Badge>}
        </div>
        <div className="text-muted-foreground mt-1 flex flex-col text-xs">
          {person.locale && (
            <span>{t("language", { locale: person.locale })}</span>
          )}
          {person.currency && (
            <span>{t("currency", { currency: person.currency })}</span>
          )}
          {person.invited_at && (
            <span>
              {t("invited_at", { date: formatDay(person.invited_at, locale) })}
            </span>
          )}
          <span>
            {person.accepted_at
              ? t("accepted_at", {
                  date: formatDay(person.accepted_at, locale),
                })
              : t("not_accepted")}
          </span>
          <span>
            {t("last_activity", {
              date: person.last_activity
                ? formatDay(person.last_activity, locale)
                : translate("resources.projects.subscribers.never"),
            })}
          </span>
          <span title={t("logins_help")}>
            {t("logins", { count: person.logins })}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!person.accepted_at && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setResending(true)}
          >
            {t("resend")}
          </Button>
        )}
        {!person.partner && (
          <Button size="sm" variant="outline" asChild>
            <Link to={`/sales/${id}`}>{t("collaborator")}</Link>
          </Button>
        )}
      </div>
      <Confirm
        isOpen={resending}
        loading={busy}
        title={t("resend")}
        content={t("resend_confirm", { email: person.email })}
        confirm={t("resend")}
        onConfirm={resend}
        onClose={() => setResending(false)}
      />

      {person.partner && sale && (
        <PartnerCard
          sale={sale}
          territories={territories ?? []}
          assignments={assignments ?? []}
          request={requests?.[0]}
          canManage
          onChange={refresh}
        />
      )}

      <MonthsBlock
        months={data.months}
        currency={currency}
        rate={rate}
        perCredit={perCredit}
      />

      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">{t("requests")}</h3>
        {data.requests.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("no_requests")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("date")}</TableHead>
                <TableHead>{t("project")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead className="text-right">{t("found")}</TableHead>
                <TableHead className="text-right">{t("credits")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.requests.map((request, index) => (
                <TableRow key={index}>
                  <TableCell>{formatDay(request.created_at, locale)}</TableCell>
                  <TableCell>
                    <Link
                      to={`/projects/${request.project_id}/show`}
                      className="underline"
                    >
                      {request.project || request.project_id.slice(0, 8)}
                    </Link>
                  </TableCell>
                  <TableCell>{t(`status_${request.status}`)}</TableCell>
                  <TableCell className="text-right">
                    {request.found ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {request.credits}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">{t("recent_questions")}</h3>
        {data.questions.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("no_questions")}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {data.questions.map((question, index) => (
              <li key={index} className="flex justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-muted-foreground">
                    {formatDay(question.created_at, locale)} ·{" "}
                  </span>
                  {question.question.length > 80
                    ? `${question.question.slice(0, 80)}…`
                    : question.question}
                </span>
                {question.cost_usd != null && (
                  <span
                    className="text-muted-foreground shrink-0 text-xs"
                    title={formatUsd(question.cost_usd, locale)}
                  >
                    {smallCost(question.cost_usd, currency, rate, locale)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Ledger lines={data.ledger} />
    </div>
  );
};

/** Coût d'une question : quelques millièmes d'euro, donc quatre décimales (la base garde la précision brute). */
const smallCost = (
  usd: number,
  currency: AccountCurrency,
  rate: number | null,
  locale: string,
) => {
  const cost = costInCurrency(usd, currency, rate);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: cost.currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(cost.value);
};

/** Six mois de dépenses par ligne, total, crédits consommés et coût par crédit du mois choisi. */
const MonthsBlock = ({
  months,
  currency,
  rate,
  perCredit,
}: {
  months: Month[];
  currency: AccountCurrency;
  rate: number | null;
  perCredit: number | null;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const t = (key: string) =>
    translate(`resources.projects.subscribers.panel.${key}`);
  const amount = (usd: number) => (
    <span title={formatUsd(usd, locale)}>
      {formatCost(usd, currency, rate, locale)}
    </span>
  );
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold">{t("months")}</h3>
      {/* Cellules resserrées : six mois et le libellé tiennent dans la largeur du panneau. */}
      <Table className="text-xs [&_td]:px-1 [&_th]:px-1">
        <TableHeader>
          <TableRow>
            <TableHead />
            {months.map((month) => (
              <TableHead key={month.month} className="text-right">
                {new Date(month.month).toLocaleDateString(locale, {
                  month: "short",
                  timeZone: "UTC",
                })}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {COST_LINES.map((line) => (
            <TableRow key={line}>
              <TableCell>{t(line)}</TableCell>
              {months.map((month) => (
                <TableCell key={month.month} className="text-right">
                  {amount(month[`${line}_usd`])}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow className="font-medium">
            <TableCell>{t("total")}</TableCell>
            {months.map((month) => (
              <TableCell key={month.month} className="text-right">
                {amount(month.cost_usd)}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>{t("credits_spent")}</TableCell>
            {months.map((month) => (
              <TableCell
                key={month.month}
                className="text-right"
                title={`${translate("resources.projects.credits.columns.emails")} ${month.emails} · ${translate("resources.projects.credits.columns.mobiles")} ${month.mobiles} · ${translate("resources.projects.credits.columns.teams")} ${month.teams}`}
              >
                {month.credits_spent}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      <p className="text-sm">
        {t("per_credit")} :{" "}
        {perCredit == null ? (
          "—"
        ) : (
          <span title={formatUsd(perCredit, locale)}>
            {formatUnitCost(perCredit, currency, rate, locale)}
          </span>
        )}
      </p>
    </div>
  );
};

const Ledger = ({
  lines,
  showPerson = false,
}: {
  lines: LedgerLine[];
  showPerson?: boolean;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  return (
    <Card>
      <CardContent className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">
          {translate("resources.projects.subscribers.panel.ledger")}
        </h3>
        {lines.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {translate("resources.projects.subscribers.panel.no_ledger")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {lines.map((line, index) => (
              <li key={index} className="flex justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-muted-foreground">
                    {formatDay(line.created_at, locale)} ·{" "}
                  </span>
                  {showPerson && `${line.person} · `}
                  {translate(
                    `resources.projects.credits.reasons.${line.reason}`,
                    { _: line.reason },
                  )}
                  {line.note && ` · ${line.note}`}
                  {line.author && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({line.author})
                    </span>
                  )}
                </span>
                <span className="shrink-0 font-medium">
                  {line.delta > 0 ? `+${line.delta}` : line.delta}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
